import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFileInput,
  WSFlexBox,
  WSFormOld,
  WSGrid,
  WSIcon,
  WSLoader,
  WSText,
  WSInputTextarea,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  VaultFileKind,
  VaultFileVisibility
} from "@wingspanhq/wingspan-api-sdk";
import React, { ChangeEvent } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Overlay } from "../../../components/Overlay";
import { Tooltip } from "../../../components/Tooltip";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { useMutationUploadVaultFile } from "../../../v3/utils/useMutationUploadVaultFile";
import {
  BILLING_ATTACHMENT_LOAD_FAILED_LABEL,
  BILLING_ATTACHMENT_UNAVAILABLE_LABEL,
  useBillingAttachmentFile
} from "../../hooks/useBillingAttachmentFile";
import { useInvoicesFormContext } from "./InvoicesForm";

export type AttachmentValues = {
  fileId: string;
  remove?: string;
};

export type OtherSectionValues = {
  other: {
    projectName: string;
    notes: string;
    attachments: AttachmentValues[];
  };
};

type Props = { fullWidth?: boolean; disabled?: boolean } & WSElementProps;

export const OtherSection: React.FC<Props> = ({
  fullWidth,
  disabled,
  ...elementProps
}) => {
  const { register, control, trigger } = useFormContext();
  const { invoicingConfigForPayee } = useInvoicesFormContext();
  const qFeatureFlags = useQueryFeatureFlags();
  const [uploadVaultFile, uploadVaultFileMeta] = useMutationUploadVaultFile();
  const {
    fields: attachmentsField,
    append,
    remove,
    insert
  } = useFieldArray<AttachmentValues>({
    control,
    name: "other.attachments"
  });

  return (
    <WSElement {...elementProps}>
      <WSGrid>
        <WSGrid.Item span={{ m: fullWidth ? "12" : "7" }}>
          <WSElement mb="XL">
            <WSFormOld.Field
              name="other.projectName"
              label={
                invoicingConfigForPayee?.requireProjectName?.enabled
                  ? "Project name*"
                  : "Project name (Optional)"
              }
              component={WSTextInput}
            />
            <WSFormOld.Error name="other.projectName" />
          </WSElement>

          <WSFormOld.Field
            mb="XL"
            name="other.notes"
            label="Notes (Optional)"
            component={WSInputTextarea}
          />

          <WSElement>
            {disabled && <Overlay white overParent />}

            {qFeatureFlags.data?.purchaseOrderNumber ? (
              <WSElement mb="XL">
                <WSFormOld.Field
                  name="purchaseOrderNumber"
                  label={
                    invoicingConfigForPayee?.requirePONumber?.enabled
                      ? "PO Number*"
                      : "PO Number (Optional)"
                  }
                  component={WSTextInput}
                />
                <WSFormOld.Error name="purchaseOrderNumber" />
              </WSElement>
            ) : null}

            <WSFormOld.Label>
              {invoicingConfigForPayee?.requireAttachments?.enabled
                ? "Attachments*"
                : "Attachments (Optional)"}
              {"  "}
              <Tooltip>
                Attachments will be accessible by anyone with the invoice link.
                Do not include sensitive information.
              </Tooltip>
            </WSFormOld.Label>

            {attachmentsField.map((attachment, index: number) =>
              attachment.fileId ? (
                <>
                  <input
                    ref={register()}
                    type="hidden"
                    name={`other.attachments[${index}].remove`}
                    defaultValue={attachment.remove}
                  />
                  <input
                    ref={register()}
                    type="hidden"
                    name={`other.attachments[${index}].fileId`}
                    defaultValue={attachment.fileId}
                  />
                  <AttachmentPreview
                    key={`${index}_${attachment.fileId}`}
                    hidden={!!attachment.remove}
                    mb="M"
                    fileId={attachment.fileId}
                    onRemove={async () => {
                      remove(index);
                      insert(index, { ...attachment, remove: "1" });
                      // manually trigger validation for attachments
                      await trigger("other.attachments");
                    }}
                  />
                </>
              ) : null
            )}

            {uploadVaultFileMeta.isLoading && <WSLoader.Spinner mb="M" />}

            <WSFileInput
              name="other.attachmentsFileUpload"
              onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                event.stopPropagation();
                event.preventDefault();
                if (event.target && event.target.files && event.target.files) {
                  const file = event.target.files[0];
                  await uploadVaultFile(
                    {
                      file,
                      kind: VaultFileKind.DOCUMENT,
                      visibility: VaultFileVisibility.PUBLIC
                    },
                    {
                      onSuccess: async vaultFile => {
                        append({
                          fileId: vaultFile.id!
                        });
                        // manually trigger validation for attachments
                        await trigger("other.attachments");
                      }
                    }
                  );
                }
              }}
            >
              <WSButton.Link size="M" type="button" icon="plus-circle">
                Attachment
              </WSButton.Link>
              <WSFormOld.Error name="other.attachments" />
            </WSFileInput>

            {uploadVaultFileMeta.isError && (
              <WSText.ParagraphSm color="garnet" my="M">
                Ooops! Something wrong with this file..
              </WSText.ParagraphSm>
            )}
          </WSElement>
        </WSGrid.Item>
      </WSGrid>
    </WSElement>
  );
};

type AttachmentProps = {
  fileId: string;
  onRemove: () => void;
} & WSElementProps;

const AttachmentPreview: React.FC<AttachmentProps> = ({
  fileId,
  onRemove,
  ...elementProps
}) => {
  const file = useBillingAttachmentFile(fileId);

  return (
    <WSElement {...elementProps}>
      {file.isLoading ? (
        <WSLoader.Spinner />
      ) : (
        <WSFlexBox.CenterY>
          {/* An unavailable attachment cannot be named, so removing it is a
              blind action — the warning icon and muted label mark it out from
              the named files sitting next to it in the list. */}
          {file.isUnavailable || file.isLoadFailed ? (
            <>
              <WSIcon block name="alert-circle" size="S" color="amber400" />
              <WSText.ParagraphSm ml="XS" mr="M" color="gray500">
                {file.isUnavailable
                  ? BILLING_ATTACHMENT_UNAVAILABLE_LABEL
                  : BILLING_ATTACHMENT_LOAD_FAILED_LABEL}
              </WSText.ParagraphSm>
            </>
          ) : (
            <WSText.ParagraphSm mr="M">
              {file.filename ?? "Attachment"}
            </WSText.ParagraphSm>
          )}
          <WSIcon
            block
            name="minus-circle"
            onClick={() => {
              onRemove();
            }}
            color="blue400"
          />
        </WSFlexBox.CenterY>
      )}
    </WSElement>
  );
};
